import React, { useState, useRef } from 'react';
import { Copy, CheckCircle2 } from 'lucide-react';
import Markdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

export const RichTextDisplay = ({ text, title = "Generated Content", hideDivider = false }: { text: string, title?: string, hideDivider?: boolean }) => {
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleCopy = async () => {
    if (!contentRef.current) return;
    
    try {
      const htmlBlob = new Blob([contentRef.current.innerHTML], { type: 'text/html' });
      const textBlob = new Blob([contentRef.current.innerText], { type: 'text/plain' });
      
      const item = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      });
      await navigator.clipboard.write([item]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      navigator.clipboard.writeText(contentRef.current.innerText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const cleanedText = text ? text.replace(/\n\s*\n/g, '\n\n').replace(/\n{3,}/g, '\n\n') : '';

  return (
    <div className={hideDivider ? "" : "mt-4 pt-4 border-t border-slate-300 dark:border-slate-800"}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">{title}</div>
        <button
          onClick={handleCopy}
          className="text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 hover:text-slate-300 px-2 py-1 flex items-center gap-1.5 transition-colors bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 rounded-md cursor-pointer"
          aria-label="Copy text"
        >
          {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied with formatting!' : 'Copy'}
        </button>
      </div>
      <div className="markdown-body p-4 lg:p-6 bg-slate-50 dark:bg-slate-950 rounded-lg border border-slate-300 dark:border-slate-800">
        <div ref={contentRef} className="prose prose-sm prose-slate dark:prose-invert prose-emerald max-w-none text-[14px]">
          <Markdown rehypePlugins={[rehypeSanitize]}>{cleanedText}</Markdown>
        </div>
      </div>
    </div>
  );
};
